import React, { useEffect, useRef, useState } from "react";
import {
  ChevronRight,
  Mail,
  MapPin,
  Phone,
  Send,
  ShieldCheck,
  Handshake,
  ArrowRight,
  Clock3,
  HelpCircle,
  Building2,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const ContactPage = () => {
  const pageRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    honeypot: "",
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
      });
      gsap.from(".contact-card", {
        opacity: 0,
        y: 60,
        duration: 1,
        stagger: 0.12,
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const submit = async (e) => {
    e.preventDefault();

    try {
      await api.post("/contact", form);
      toast.success("Message sent successfully");

      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        honeypot: "",
      });
    } catch (err) {
      toast.error(err.cleanMessage || "Message failed to send");
    }
  };

  return (
    <main ref={pageRef}>
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="Contact OGFZA"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />
          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Get In Touch
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Contact Us
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Contact the Oil and Gas Free Zones Authority for enquiries,
              customer support, regulatory complaints, investor assistance and
              Free Zone service information.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Contact Us</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Link
              to="/contact-help/complaints"
              className="contact-card group bg-white rounded-[30px] p-7 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-5 group-hover:bg-[#ff6600] group-hover:text-white transition-all">
                <ShieldCheck size={26} />
              </div>

              <h3
                className="text-2xl font-black text-[#06183f]"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Regulatory Complaint Portal
              </h3>

              <p className="text-slate-600 mt-3 leading-relaxed">
                Submit complaints relating to licensing, approvals, tariffs,
                operators, HSE, logistics, service delivery, unethical conduct
                and other Free Zone regulatory matters.
              </p>

              <span className="inline-flex items-center gap-2 mt-5 text-[#06183f] font-bold border-b-2 border-[#06183f] pb-1 group-hover:text-[#ff6600] group-hover:border-[#ff6600]">
                Submit complaint <ArrowRight size={18} />
              </span>
            </Link>

            <Link
              to="/contact-help/track-complaint"
              className="contact-card group bg-white rounded-[30px] p-7 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center mb-5 group-hover:bg-[#06183f] group-hover:text-white transition-all">
                <FileIcon />
              </div>

              <h3
                className="text-2xl font-black text-[#06183f]"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Track Complaint
              </h3>

              <p className="text-slate-600 mt-3 leading-relaxed">
                Use your OGFZA complaint reference number to check the current
                status, review stage, response note and resolution update.
              </p>

              <span className="inline-flex items-center gap-2 mt-5 text-[#06183f] font-bold border-b-2 border-[#06183f] pb-1 group-hover:text-[#ff6600] group-hover:border-[#ff6600]">
                Track status <ArrowRight size={18} />
              </span>
            </Link>

            <Link
              to="/contact-help/faq"
              className="contact-card group bg-white rounded-[30px] p-7 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-5 group-hover:bg-[#ff6600] group-hover:text-white transition-all">
                <HelpCircle size={26} />
              </div>

              <h3
                className="text-2xl font-black text-[#06183f]"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Frequently Asked Questions
              </h3>

              <p className="text-slate-600 mt-3 leading-relaxed">
                Find answers on registration, Free Zone licences, renewals,
                HSE certificates, work permits, fees, cargo processing and
                service timelines.
              </p>

              <span className="inline-flex items-center gap-2 mt-5 text-[#06183f] font-bold border-b-2 border-[#06183f] pb-1 group-hover:text-[#ff6600] group-hover:border-[#ff6600]">
                View FAQs <ArrowRight size={18} />
              </span>
            </Link>

            <Link
              to="/spv/contact"
              className="contact-card group bg-white rounded-[30px] p-7 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center mb-5 group-hover:bg-[#06183f] group-hover:text-white transition-all">
                <Handshake size={26} />
              </div>

              <h3
                className="text-2xl font-black text-[#06183f]"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                SPV & Investment Desk
              </h3>

              <p className="text-slate-600 mt-3 leading-relaxed">
                Reach the SPV team for investment engagement, strategic
                partnerships, PPP facilitation and Free Zone development
                support.
              </p>

              <span className="inline-flex items-center gap-2 mt-5 text-[#06183f] font-bold border-b-2 border-[#06183f] pb-1 group-hover:text-[#ff6600] group-hover:border-[#ff6600]">
                Contact SPV <ArrowRight size={18} />
              </span>
            </Link>
          </div>

          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8">
            <div className="space-y-5">
              {[
                {
                  icon: MapPin,
                  title: "Head Office",
                  text: "Marble House, Federal Ocean Terminal (FOT), PMB 5474, Onne Port Complex, Rivers State, Nigeria",
                },
                {
                  icon: Mail,
                  title: "Email",
                  text: "contact@ogfza.gov.ng",
                },
                {
                  icon: Phone,
                  title: "Phone",
                  text: "+234 909 111 1184, +234 909 111 1185",
                },
                {
                  icon: Clock3,
                  title: "Working Hours",
                  text: "Monday - Friday, 9:00 AM - 5:00 PM",
                },
              ].map(({ icon: Icon, title, text }) => (
                <div
                  key={title}
                  className="contact-card bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)]"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>

                  <h3
                    className="font-black text-[#06183f]"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {title}
                  </h3>

                  <p className="text-slate-600 mt-2 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            <form
              onSubmit={submit}
              className="contact-card bg-white rounded-[34px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] p-6 md:p-8"
            >
              <input
                type="text"
                value={form.honeypot}
                onChange={(e) =>
                  setForm({ ...form, honeypot: e.target.value })
                }
                className="hidden"
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="mb-6">
                <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-4">
                  <Building2 size={26} />
                </div>

                <h2
                  className="text-3xl font-black text-[#06183f]"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Send A General Enquiry
                </h2>

                <p className="text-slate-600 mt-2 leading-relaxed">
                  Use this form for general messages. For regulatory complaints,
                  use the complaint portal so your complaint can receive a
                  reference number and tracking status.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <input
                  required
                  placeholder="Full Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
                />

                <input
                  required
                  type="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
                />

                <input
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
                />

                <input
                  required
                  placeholder="Subject"
                  value={form.subject}
                  onChange={(e) =>
                    setForm({ ...form, subject: e.target.value })
                  }
                  className="border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
                />
              </div>

              <textarea
                required
                rows="6"
                placeholder="Message"
                value={form.message}
                onChange={(e) =>
                  setForm({ ...form, message: e.target.value })
                }
                className="mt-4 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] resize-none"
              />

              <button className="mt-5 inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-[#06183f] hover:bg-[#ff6600] text-white font-black transition-colors">
                Send Message <Send size={18} />
              </button>
            </form>
          </div>

          <div className="mt-10">
            <iframe
              title="OGFZA Head Office Map"
              className="w-full h-[430px] rounded-[34px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)]"
              loading="lazy"
              src="https://www.google.com/maps?q=Marble%20House%20Federal%20Ocean%20Terminal%20Onne%20Port%20Complex%20Rivers%20State%20Nigeria&output=embed"
            />
          </div>
        </div>
      </section>
    </main>
  );
};

const FileIcon = () => <ShieldCheck size={26} />;

export default ContactPage;